import type * as React from 'react'
import { LinkPlugin } from '@platejs/link/react'

import { RichTextFieldBasicMarksKit } from '../basic-marks'
import { RichTextFieldParagraphPlugin } from '../paragraph'
import { LinkElement } from './LinkElement'
import type { RichTextFieldLinkTarget } from './LinkPopover'

const DEFAULT_ALLOWED_SCHEMES = Object.freeze([
  'http',
  'https',
  'mailto',
  'tel',
])

type ValidateLinkUrlOptions = {
  allowedSchemes?: readonly string[]
  isUrl?: (url: string) => boolean
}

type RichTextFieldLinkPluginOptions = ValidateLinkUrlOptions & {
  defaultLinkAttributes?: React.AnchorHTMLAttributes<HTMLAnchorElement>
  target?: RichTextFieldLinkTarget
}

const validateLinkUrl = (
  url: string,
  {
    allowedSchemes = DEFAULT_ALLOWED_SCHEMES,
    isUrl,
  }: ValidateLinkUrlOptions = {},
): boolean => {
  const normalizedUrl = url.trim()
  const scheme = /^([a-z][a-z\d+.-]*):/i.exec(normalizedUrl)?.[1]

  if (!scheme) return false
  if (!allowedSchemes.includes(scheme.toLowerCase())) return false

  return isUrl?.(normalizedUrl) ?? true
}

const createRichTextFieldLinkPlugin = ({
  allowedSchemes = DEFAULT_ALLOWED_SCHEMES,
  defaultLinkAttributes,
  isUrl,
  target = '_blank',
}: RichTextFieldLinkPluginOptions = {}): typeof LinkPlugin => {
  const schemes = allowedSchemes.map((scheme) => scheme.trim().toLowerCase())

  return LinkPlugin.configure({
    options: {
      allowedSchemes: schemes,
      defaultLinkAttributes: {
        rel: 'noopener noreferrer',
        ...defaultLinkAttributes,
        target: defaultLinkAttributes?.target ?? target,
      },
      isUrl: (url) => validateLinkUrl(url, { allowedSchemes: schemes, isUrl }),
    },
  }).withComponent(LinkElement)
}

const RichTextFieldLinkPlugin = createRichTextFieldLinkPlugin()

const RichTextFieldBaseKit = [
  RichTextFieldParagraphPlugin,
  ...RichTextFieldBasicMarksKit,
  RichTextFieldLinkPlugin,
]

export { LinkElement } from './LinkElement'
export { LinkPopover } from './LinkPopover'
export type { LinkPopoverProps, RichTextFieldLinkTarget } from './LinkPopover'
export { LinkToolbarButton } from './LinkToolbarButton'
export type { LinkToolbarButtonProps } from './LinkToolbarButton'
export {
  DEFAULT_ALLOWED_SCHEMES,
  RichTextFieldBaseKit,
  RichTextFieldBasicMarksKit,
  RichTextFieldLinkPlugin,
  createRichTextFieldLinkPlugin,
  validateLinkUrl,
}
export type { RichTextFieldLinkPluginOptions, ValidateLinkUrlOptions }
